import { TranslateFunction } from '@locales/generated';
import { sortedBy } from '@shared/utils/sort';
import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { mapToLabel } from './components/Filters/facetOptions';
import { Facet } from './components/Filters/Filter.types';
import { FACET_ORDER } from './components/Filters/Filters.constants';
import { StationeryTemplate } from './DesignsGallery.types';

export const useFilterFacets = (templates: StationeryTemplate[]) => {
  const { t } = useTranslation('stationery');
  const tFilterOptions = t('dashboard', 'designsGallery', 'filterOptions') as Record<string, TranslateFunction>;

  return useMemo(() => {
    const facetOptionCounts = new Map<string, number>();
    for (const option of templates.flatMap(t => t.mappedTags)) {
      facetOptionCounts.set(option, (facetOptionCounts.get(option) ?? 0) + 1);
    }

    const facets = new Map<Facet, typeof facetOptionCounts>();
    for (const [facetOption, count] of Array.from(facetOptionCounts.entries())) {
      const facet = facetOption.split(':')[0] as Facet;
      facets.set(facet, (facets.get(facet) ?? new Map()).set(facetOption, count));
    }

    const facetsForUI = Array.from(facets.entries()).map(([facet, options]) => {
      return {
        groupCode: facet,
        label: tFilterOptions[facet]?.() ?? `[${facet}]`,
        options: Array.from(options.entries()).map(([option, count]) => {
          const translationKey = option.replace(':', ' '); // Don't use colon as separator, i18next will think it's a namespace
          return {
            compositeCode: option,
            label: tFilterOptions[translationKey]?.() ?? mapToLabel(option) ?? `[${translationKey}]`,
            matchingItemCount: count
          };
        })
      };
    });

    for (const facet of facetsForUI) {
      facet.options.sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
    }
    return sortedBy(facetsForUI, f => f.groupCode, FACET_ORDER);
  }, [tFilterOptions, templates]);
};
